import { OverflowList } from '@theme/overflow-list';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#updateBadges(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages();
    this.#previousSlideIndex = null;

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates product badges based on the selected variant state.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateBadges(event) {
    const currentBadges = this.querySelector('.product-badges');
    const newBadges = event.detail.data.html?.querySelector('.product-badges');

    if (!currentBadges || !newBadges) return;

    morph(currentBadges, newBadges);
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const anchorElement = event.detail.data.html?.querySelector('product-card a');
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector('product-card-link')
      ?.getAttribute('data-featured-media-url');

    // If the product card is inside a product link, update the product link's featured media URL
    if (featuredMediaUrl && this.closest('product-card-link'))
      this.closest('product-card-link')?.setAttribute('data-featured-media-url', featuredMediaUrl);

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }

      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();
    slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
    } else {
      slideshow.next(undefined, { animate: false });
      setTimeout(() => this.#preloadNextPreviewImage());
    }
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!this.variantPicker) {
      if (!slideshow) return;
      slideshow.previous(undefined, { animate: false });
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    // If we have a selected variant, always use its image
    if (this.variantPicker?.selectedOption) {
      const id = this.variantPicker.selectedOption.dataset.optionMediaId;
      if (id) {
        slideshow.select({ id }, undefined, { animate: false });
        return;
      }
    }

    // No variant selected - use initial slide if it's valid
    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    // No valid initial slide or selected variant - go to previous
    slideshow.previous(undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const blockedElement = event.target.closest(
      [
        'button',
        'input',
        'label',
        'select',
        '[tabindex="1"]',
        'quick-add-component',
        '.quick-add',
        '.quick-add__button',
        '.quick-add__size-selector',
        '.quick-add__size-pill',
        'slideshow-arrows',
        '.slideshow-arrows',
        '.slideshow-control',
        'swatches-variant-picker-component',
        'product-colour-swatches',
      ].join(', ')
    );

    // Ignore clicks on interactive overlays/controls (arrows, size selector, swatches, quick add).
    if (blockedElement) {
      return;
    }

    const allowedClickTarget = event.target.closest(
      [
        '[ref="productTitleLink"]',
        '[data-product-card-title]',
        '.product-card-title__main',
        '.product-card-title__subtitle',
        'product-price',
        '[ref="cardGalleryLink"]',
        '.card-gallery',
      ].join(', ')
    );

    // Prevent accidental navigation from non-target areas.
    if (!allowedClickTarget) return;

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const infiniteResultsList = this.closest('results-list[infinite-scroll="true"]');
    if (!window.Shopify.designMode && infiniteResultsList) {
      const url = new URL(window.location.href);
      const parent = this.closest('li');
      url.hash = productCardAnchor;
      if (parent && parent.dataset.page) {
        url.searchParams.set('page', parent.dataset.page);
      }

      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that updates product-card content from colour swatch selections.
 */
class ProductColourSwatches extends Component {
  /** @type {AbortController | null} */
  #sectionRequestAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#handleSwatchClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleSwatchClick);
    this.#sectionRequestAbortController?.abort();
    this.#sectionRequestAbortController = null;
  }

  /**
   * Handles swatch click.
   * @param {Event} event
   */
  #handleSwatchClick = async (event) => {
    if (!(event.target instanceof Element)) return;

    const swatchButton = event.target.closest('button[data-swatch-product-url]');
    if (!(swatchButton instanceof HTMLButtonElement)) return;

    event.preventDefault();
    this.#setSelectedSwatchState(swatchButton);
    await this.#applySwatchSelection(swatchButton);
  };

  /**
   * Sets active state for swatch buttons.
   * @param {HTMLButtonElement} selectedButton
   */
  #setSelectedSwatchState(selectedButton) {
    const swatchButtons = this.querySelectorAll('button[data-swatch-product-url]');

    for (const swatchButton of swatchButtons) {
      const isSelected = swatchButton === selectedButton;
      swatchButton.classList.toggle('is-selected', isSelected);
      swatchButton.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    }
  }

  /**
   * Applies selected swatch content to the surrounding product card.
   * @param {HTMLButtonElement} swatchButton
   */
  async #applySwatchSelection(swatchButton) {
    const productUrl = swatchButton.dataset.swatchProductUrl;
    const productCard = this.closest('product-card');
    const quickAddModalContent = this.closest('#quick-add-modal-content');

    if (quickAddModalContent instanceof HTMLElement) {
      await this.#applyQuickAddModalSwatchSelection(swatchButton, productUrl);
      return;
    }

    // PDP/other contexts: if not inside a product-card, navigate to the selected product URL.
    if (!(productCard instanceof HTMLElement)) {
      if (productUrl) {
        window.location.href = productUrl;
      }
      return;
    }

    const productTitle = swatchButton.dataset.swatchProductTitle;
    const productId = swatchButton.dataset.swatchProductId;
    const productImageUrl = swatchButton.dataset.swatchImageUrl;
    const productImageAlt = swatchButton.dataset.swatchImageAlt;
    const featuredMediaUrl = swatchButton.dataset.swatchFeaturedMediaUrl;
    const productPriceHtml = swatchButton.dataset.swatchPriceHtml;
    const productAvailable = swatchButton.dataset.swatchProductAvailable;

    this.#updateLinks(productCard, productUrl);
    this.#updateTitle(productCard, productTitle);
    this.#updateImage(productCard, productImageUrl, productImageAlt);
    this.#updatePrice(productCard, productPriceHtml);
    this.#updateProductIds(productCard, productId);
    if (!this.#updateBadgesFromSwatch(productCard, swatchButton)) {
      this.#updateSoldOutBadgeState(productCard, productAvailable !== 'false');
    }

    const productCardLink = productCard.closest('product-card-link');
    if (productCardLink instanceof HTMLElement && featuredMediaUrl) {
      productCardLink.setAttribute('data-featured-media-url', featuredMediaUrl);
    }

    const sectionHtml = await this.#fetchProductCardSection(productUrl);
    if (sectionHtml) {
      this.#updateTitleFromSection(productCard, sectionHtml, productTitle);
      this.#updateLinksFromSection(productCard, sectionHtml);
      this.#updatePriceFromSection(productCard, sectionHtml);
      this.#updateBadgesFromSection(productCard, sectionHtml);
      this.#updateQuickAddFromSection(productCard, sectionHtml);
      this.#updateSoldOutBadgeState(productCard, productAvailable !== 'false');
    }

    if (productCard instanceof ProductCard) {
      productCard.refs.quickAdd?.fetchProductPage(productCard.productPageUrl);
    }
  }

  /**
   * Applies selected colour swatches inside the quick buy modal.
   * @param {HTMLButtonElement} swatchButton
   * @param {string | undefined} productUrl
   */
  async #applyQuickAddModalSwatchSelection(swatchButton, productUrl) {
    if (!productUrl) return;

    const dialog = document.getElementById('quick-add-dialog');
    const activeQuickAdd = dialog?.activeQuickAdd;
    if (!activeQuickAdd) {
      window.location.href = productUrl;
      return;
    }

    const html = await activeQuickAdd.fetchProductPage(productUrl);
    const gridElement = html?.querySelector('[data-product-grid-content]');
    if (!gridElement) return;

    await activeQuickAdd.updateQuickAddModal(/** @type {Element} */ (gridElement.cloneNode(true)), {
      selectedProductId: swatchButton.dataset.swatchProductId || '',
      productUrl,
    });
  }

  /**
   * Updates product-card links to the selected product.
   * @param {HTMLElement} productCard
   * @param {string | undefined} productUrl
   */
  #updateLinks(productCard, productUrl) {
    if (!productUrl) return;

    const linkTargets = productCard.querySelectorAll('a[ref="productCardLink"], a[ref="productTitleLink"], a[ref="cardGalleryLink"]');
    for (const linkTarget of linkTargets) {
      if (linkTarget instanceof HTMLAnchorElement) {
        linkTarget.href = productUrl;
      }
    }
  }

  /**
   * Updates product title text in card.
   * @param {HTMLElement} productCard
   * @param {string | undefined} productTitle
   */
  #updateTitle(productCard, productTitle) {
    if (!productTitle) return;

    const titleElement = productCard.querySelector('[data-product-card-title]');
    if (titleElement instanceof HTMLElement) {
      const { main, subtitle, delimiter } = this.#splitProductTitle(
        productTitle,
        this.#getTitleSplitDelimiters(titleElement)
      );

      titleElement.replaceChildren();
      if (delimiter) {
        titleElement.dataset.titleSplitDelimiter = delimiter;
      } else {
        delete titleElement.dataset.titleSplitDelimiter;
      }

      const mainElement = document.createElement('span');
      mainElement.className = 'product-card-title__main';
      mainElement.textContent = main;
      titleElement.appendChild(mainElement);

      if (subtitle) {
        const subtitleElement = document.createElement('span');
        const pushSplitToBottom = productCard.querySelector('.product-title-block--push-split-bottom');
        subtitleElement.className = pushSplitToBottom
          ? 'product-card-title__subtitle product-card-title__subtitle--push-bottom'
          : 'product-card-title__subtitle';
        subtitleElement.textContent = subtitle;
        titleElement.appendChild(subtitleElement);
      }
    }

    const hiddenTitle = productCard.querySelector('.product-card__link .visually-hidden');
    if (hiddenTitle instanceof HTMLElement) {
      hiddenTitle.textContent = productTitle;
    }
  }

  /**
   * Returns the configured list of title split delimiters.
   * @param {HTMLElement} titleElement
   * @returns {string[]}
   */
  #getTitleSplitDelimiters(titleElement) {
    const configuredDelimiters = titleElement.dataset.titleSplitDelimiters || '';
    const delimiters = configuredDelimiters
      .split(',')
      .filter((delimiter) => delimiter.length > 0);

    return delimiters.length > 0 ? delimiters : [' in '];
  }

  /**
   * Splits product title on the last matching delimiter into title + subtitle.
   * @param {string} title
   * @param {string[]} delimiters
   * @returns {{ main: string; subtitle: string; delimiter: string }}
   */
  #splitProductTitle(title, delimiters = [' in ']) {
    const normalizedTitle = title.trim();
    let bestSplitIndex = -1;
    let bestDelimiter = '';

    for (const delimiter of delimiters) {
      const splitIndex = normalizedTitle.lastIndexOf(delimiter);

      if (splitIndex > bestSplitIndex) {
        bestSplitIndex = splitIndex;
        bestDelimiter = delimiter;
      }
    }

    if (bestSplitIndex === -1 || !bestDelimiter) {
      return { main: normalizedTitle, subtitle: '', delimiter: '' };
    }

    const main = normalizedTitle.slice(0, bestSplitIndex).trim();
    const subtitle = normalizedTitle.slice(bestSplitIndex + bestDelimiter.length).trim();

    if (!main || !subtitle) {
      return { main: normalizedTitle, subtitle: '', delimiter: '' };
    }

    return { main, subtitle, delimiter: bestDelimiter };
  }

  /**
   * Updates card image to selected product featured image.
   * @param {HTMLElement} productCard
   * @param {string | undefined} imageUrl
   * @param {string | undefined} imageAlt
   */
  #updateImage(productCard, imageUrl, imageAlt) {
    if (!imageUrl) return;

    const imageSelectorPriority = [
      '.card-gallery img[transitionToProduct="true"]',
      '.card-gallery img[transitiontoproduct="true"]',
      '.card-gallery .product-media__image',
      '.card-gallery img',
    ];

    /** @type {HTMLImageElement[]} */
    let images = [];

    for (const selector of imageSelectorPriority) {
      const matchedImages = Array.from(productCard.querySelectorAll(selector)).filter(
        (element) => element instanceof HTMLImageElement
      );
      if (matchedImages.length > 0) {
        images = matchedImages;
        break;
      }
    }

    for (const image of images) {
      if (!(image instanceof HTMLImageElement)) continue;
      image.src = imageUrl;
      image.srcset = imageUrl;
      image.dataset.maxResolution = imageUrl;
      if (imageAlt) {
        image.alt = imageAlt;
      }
    }
  }

  /**
   * Updates card price markup.
   * @param {HTMLElement} productCard
   * @param {string | undefined} priceHtml
   */
  #updatePrice(productCard, priceHtml) {
    if (!priceHtml) return;

    const priceRoot = productCard.querySelector('product-price');
    if (!(priceRoot instanceof HTMLElement)) return;

    const currentPriceContainer = priceRoot.querySelector('[ref="priceContainer"]');
    if (!(currentPriceContainer instanceof HTMLElement)) return;

    const template = document.createElement('template');
    template.innerHTML = priceHtml;

    const nextPriceContainer = template.content.querySelector('[ref="priceContainer"]');
    if (nextPriceContainer) {
      currentPriceContainer.replaceWith(nextPriceContainer);
    }

    const currentVolumePricingNote = priceRoot.querySelector('.volume-pricing-note');
    const nextVolumePricingNote = template.content.querySelector('.volume-pricing-note');

    if (!nextVolumePricingNote) {
      currentVolumePricingNote?.remove();
      return;
    }

    if (!currentVolumePricingNote) {
      priceRoot.querySelector('[ref="priceContainer"]')?.insertAdjacentElement('afterend', nextVolumePricingNote);
      return;
    }

    currentVolumePricingNote.replaceWith(nextVolumePricingNote);
  }

  /**
   * Fetches fresh product-card render from Section Rendering API.
   * @param {string | undefined} productUrl
   * @returns {Promise<Document | null>}
   */
  async #fetchProductCardSection(productUrl) {
    if (!productUrl) return null;

    const requestUrl = this.#buildSectionRenderUrl(productUrl);
    if (!requestUrl) return null;

    this.#sectionRequestAbortController?.abort();
    this.#sectionRequestAbortController = new AbortController();

    try {
      const response = await fetch(requestUrl, { signal: this.#sectionRequestAbortController.signal });
      if (!response.ok) return null;

      const responseText = await response.text();
      return new DOMParser().parseFromString(responseText, 'text/html');
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return null;
      console.error(error);
      return null;
    } finally {
      this.#sectionRequestAbortController = null;
    }
  }

  /**
   * @param {string} productUrl
   * @returns {string}
   */
  #buildSectionRenderUrl(productUrl) {
    try {
      const productDestination = new URL(productUrl, window.location.origin);
      const url = new URL(
        `${productDestination.pathname}${productDestination.search}`,
        window.location.origin
      );
      url.searchParams.set('section_id', 'section-rendering-product-card');
      return url.toString();
    } catch {
      return '';
    }
  }

  /**
   * Swaps in the badges rendered for the swatch's product (badge text, sold out, sale, tags).
   * @param {HTMLElement} productCard
   * @param {HTMLButtonElement} swatchButton
   * @returns {boolean} Whether the swatch carried badges to use.
   */
  #updateBadgesFromSwatch(productCard, swatchButton) {
    const badgesHtml = swatchButton.dataset.swatchBadgesHtml;
    if (!badgesHtml) return false;

    const template = document.createElement('template');
    template.innerHTML = badgesHtml;
    const nextBadges = template.content.querySelector('.product-badges');
    if (!(nextBadges instanceof HTMLElement)) return false;

    const currentBadges = productCard.querySelector('.product-badges');
    if (currentBadges instanceof HTMLElement) {
      currentBadges.replaceWith(nextBadges);
      return true;
    }

    const cardGallery = productCard.querySelector('.card-gallery');
    if (!(cardGallery instanceof HTMLElement)) return false;
    cardGallery.append(nextBadges);
    return true;
  }

  /**
   * Ensures sold-out badge reflects the selected swatch even when section fetch is unavailable.
   * @param {HTMLElement} productCard
   * @param {boolean} isAvailable
   */
  #updateSoldOutBadgeState(productCard, isAvailable) {
    const cardGallery = productCard.querySelector('.card-gallery');
    if (!(cardGallery instanceof HTMLElement)) return;

    let badges = productCard.querySelector('.product-badges');
    // Product Badge Text or an OOS Message replaces the sold out badge.
    if (badges?.hasAttribute('data-hide-sold-out')) return;
    const soldOutBadge = badges?.querySelector('.product-badges__badge--sold-out');

    if (isAvailable) {
      soldOutBadge?.remove();
      if (badges instanceof HTMLElement && badges.childElementCount === 0) {
        badges.remove();
      }
      return;
    }

    if (!(badges instanceof HTMLElement)) {
      badges = document.createElement('div');
      badges.className = 'product-badges product-badges--top-left';
      cardGallery.append(badges);
    }

    if (soldOutBadge) return;

    const soldOutText = document.querySelector('.product-badges__badge--sold-out')?.textContent?.trim() || 'Sold out';
    const badge = document.createElement('div');
    badge.className = 'product-badges__badge product-badges__badge--rectangle product-badges__badge--sold-out';
    badge.textContent = soldOutText;
    badges.append(badge);
  }

  /**
   * Updates card links from fresh rendered section response.
   * @param {HTMLElement} productCard
   * @param {Document} sectionHtml
   */
  #updateLinksFromSection(productCard, sectionHtml) {
    const sectionAnchor = sectionHtml.querySelector('product-card a[ref="productCardLink"]');
    if (!(sectionAnchor instanceof HTMLAnchorElement)) return;

    this.#updateLinks(productCard, sectionAnchor.href);
  }

  /**
   * Updates price from fresh rendered section response.
   * @param {HTMLElement} productCard
   * @param {Document} sectionHtml
   */
  #updatePriceFromSection(productCard, sectionHtml) {
    const nextPriceRoot = sectionHtml.querySelector('product-price');
    if (!(nextPriceRoot instanceof HTMLElement)) return;

    this.#updatePrice(productCard, nextPriceRoot.innerHTML);
  }

  /**
   * Updates badges from fresh rendered section response.
   * @param {HTMLElement} productCard
   * @param {Document} sectionHtml
   */
  #updateBadgesFromSection(productCard, sectionHtml) {
    const currentBadges = productCard.querySelector('.product-badges');
    const nextBadges = sectionHtml.querySelector('.product-badges');

    if (currentBadges instanceof HTMLElement && nextBadges instanceof HTMLElement) {
      morph(currentBadges, nextBadges);
      return;
    }

    if (!(currentBadges instanceof HTMLElement) && nextBadges instanceof HTMLElement) {
      const cardGallery = productCard.querySelector('.card-gallery');
      if (cardGallery instanceof HTMLElement) {
        cardGallery.append(nextBadges);
      }
    }
  }

  /**
   * Updates title from fresh rendered section response and reapplies split logic.
   * @param {HTMLElement} productCard
   * @param {Document} sectionHtml
   * @param {string | undefined} fallbackTitle
   */
  #updateTitleFromSection(productCard, sectionHtml, fallbackTitle) {
    const sectionTitleElement = sectionHtml.querySelector('[data-product-card-title]');
    const sectionTitleText = sectionTitleElement?.textContent?.replace(/\s+/g, ' ').trim() || '';
    const resolvedTitle = fallbackTitle || sectionTitleText;

    if (!resolvedTitle) return;

    this.#updateTitle(productCard, resolvedTitle);
  }

  /**
   * Updates quick-add component (size options, state, variant ids) from fresh rendered section response.
   * @param {HTMLElement} productCard
   * @param {Document} sectionHtml
   */
  #updateQuickAddFromSection(productCard, sectionHtml) {
    const currentQuickAdd = productCard.querySelector('quick-add-component');
    const nextQuickAdd = sectionHtml.querySelector('quick-add-component');

    if (!(currentQuickAdd instanceof HTMLElement) || !(nextQuickAdd instanceof HTMLElement)) return;

    morph(currentQuickAdd, nextQuickAdd);
  }

  /**
   * Updates product-id datasets to match selected swatch product.
   * @param {HTMLElement} productCard
   * @param {string | undefined} productId
   */
  #updateProductIds(productCard, productId) {
    if (!productId) return;

    productCard.dataset.productId = productId;
    this.dataset.productId = productId;

    const priceComponent = productCard.querySelector('product-price');
    if (priceComponent instanceof HTMLElement) {
      priceComponent.dataset.productId = productId;
    }

    const productCardLink = productCard.closest('product-card-link');
    if (productCardLink instanceof HTMLElement) {
      productCardLink.setAttribute('data-product-id', productId);
    }
  }
}

if (!customElements.get('product-colour-swatches')) {
  customElements.define('product-colour-swatches', ProductColourSwatches);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
